import {AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Designer} from '@pdfme/ui';
import { Template } from '@pdfme/common';
import {text, image, line} from '@pdfme/schemas';
import {firstValueFrom, Subscription} from 'rxjs';
import {CertificateTemplateService} from '../../../../core/services/certificateTemplate.service';
import {FuseConfigService} from '../../../../core/services/config.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {DocumentsService} from '../../../../core/services/documents.service';
import {TranslateService} from '@ngx-translate/core';
import {Location} from '@angular/common';
import { Font } from '@pdfme/common';
import {MatDialog} from '@angular/material/dialog';
import {FuseDeleteDialogComponent} from '../../../../core/components/delete-dialog/delete-dialog.component';
import { generate } from '@pdfme/generator';

export enum CertificateTemplateField  {
  UserFullName = 'user.fullName',
  EventName = 'event.name',
  EventDate = 'event.date',
  OrganizationName = 'organization.name',
}
interface CertificateFieldConfig {
  field: CertificateTemplateField;
  label: string;
  preview: string;
}

@Component({
  selector: 'app-certificate-detail',
  templateUrl: './certificate-detail.component.html',
  styleUrls: ['./certificate-detail.component.scss'],
  standalone: false
})
export class CertificateDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private fuseConfigService = inject(FuseConfigService);
  private spinner = inject(NgxSpinnerService);
  private documentService = inject(DocumentsService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private location = inject(Location);
  private dialog = inject(MatDialog);

  public organizationId = this.fuseConfigService.organizationId_Sig;

  @ViewChild('designerContainer', {static: true})
  designerContainer!: ElementRef<HTMLDivElement>;

  name = '';
  id?: number;

  isPortrait = false;

  private templateSubscription?: Subscription;
  private designer?: Designer;
  private readonly BACKGROUND_SCHEMA_NAME = '__certificate_background__';
  private selectedBackgroundFile?: File;
  private imageFiles = new Map<string, File>();
  private originalAssetIds = new Set<string>();
  readonly dynamicFields: CertificateFieldConfig[] = [
    {
      field: CertificateTemplateField.UserFullName,
      label: 'certificate.user_name',
      preview: 'User name',
    },
    {
      field: CertificateTemplateField.EventName,
      label: 'certificate.event_name',
      preview: 'Example Training Event',
    },
    {
      field: CertificateTemplateField.EventDate,
      label: 'certificate.event_date',
      preview: '31. Oktober 2026',
    },
    {
      field: CertificateTemplateField.OrganizationName,
      label: 'certificate.organization_name',
      preview: 'organization name',
    },
  ];

  constructor(private certificateTemplateService: CertificateTemplateService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.queryParamMap.get('id'));

    if (!Number.isSafeInteger(id) || id <= 0) {
      return;
    }

    this.id = id;
  }

  async ngAfterViewInit(): Promise<void> {
    const font = await this.loadCertificateFonts();
    const template: Template = {
      basePdf: {
        width: 297,
        height: 210,
        padding: [0, 0, 0, 0],
      },
      schemas: [[]],
    };

    this.designer = new Designer({
      domContainer: this.designerContainer.nativeElement,
      template,
      plugins: {
        Text: text,
        Image: image,
        Line: line,
      },
      options: {
        zoomLevel: 1,
        sidebarOpen: true,
        font
      },
    });

    if (this.id) {
      void this.loadTemplate(this.id);
    }
  }

  private async loadTemplate(id: number): Promise<void> {
      if (!this.designer) {
        return;
      }
      this.spinner.show();

      const result: any = await firstValueFrom(
        this.certificateTemplateService.template(id),
      );

      this.name = result.name;

      const template: any = structuredClone(result.template);
      this.isPortrait = template.basePdf.width < template.basePdf.height;
      this.rememberOriginalAssets(template);

      await this.loadTemplateImages(template);

      this.designer.updateTemplate(template);
      this.spinner.hide();
    }

  private async loadTemplateImages(template: Template): Promise<void> {
    for (const page of template.schemas) {
      for (const schema of page) {
        const assetId = (schema as {assetId?: string | number}).assetId;

        if (schema.type !== 'image' || !assetId) {
          continue;
        }

        const url = await firstValueFrom(
          this.certificateTemplateService.getImage(Number(assetId)),
        );
        const blob = await fetch(url).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to load certificate image: ${response.status}`);
          }

          return response.blob();
        });
        const base64 = await this.blobToDataUrl(blob);

        schema.content = base64;
      }
    }
  }

  async save(): Promise<void> {
    if (!this.designer || !this.name?.trim()) {
      return;
    }
    const template = this.designer.getTemplate();

    let backgroundAssetId: string | undefined;

    if (this.selectedBackgroundFile) {
      const asset = await firstValueFrom(
        this.certificateTemplateService.uploadImage(
          this.selectedBackgroundFile,
          undefined,
          'certificate_background'
        ),
      );

      backgroundAssetId = asset.id;
    }


    const imageAssetIds = new Map<string, string>();

    for (const page of template.schemas) {
      for (const schema of page) {
        if (schema.type !== 'image' || !schema.name.startsWith('image_')) {
          continue;
        }

        const imageId = schema.name.substring('image_'.length,);

        const file = this.imageFiles.get(imageId);

        if (!file) {
          continue;
        }

        const asset = await firstValueFrom(
          this.certificateTemplateService.uploadImage(
            file,
            undefined,
            'certificate_image'
          ));

        imageAssetIds.set(
          schema.name,
          asset.id,
        );
      }
    }

    const templateToSave = structuredClone(template) as any;
    console.log(templateToSave);
    for (const page of templateToSave.schemas) {
      for (const schema of page) {
        if (schema.type !== 'image') {
          continue;
        }
        if (schema.name === this.BACKGROUND_SCHEMA_NAME && backgroundAssetId) {
          schema.assetId = backgroundAssetId;
        }

        const newImageAssetId = imageAssetIds.get(schema.name);
        if (newImageAssetId) {
          schema.assetId = newImageAssetId;
        }
        delete schema.content;
      }
    }

    await firstValueFrom(
      this.certificateTemplateService.saveTemplate({
        ...(this.id ? { id: this.id } : {}),
        name: this.name,
        template: templateToSave,
        organizationId: +this.organizationId() ? +this.organizationId() : 226
      }),
    );

    const currentAssetIds = this.getAssetIds(templateToSave);
    const removedAssetIds = new Set(this.originalAssetIds);
    removedAssetIds.forEach(id => {
      if (!currentAssetIds.has(id)) {
        this.documentService.deleteDocument(id).subscribe();
      }
    });

    this.originalAssetIds.clear();
    this.rememberOriginalAssets(templateToSave);
    this.router.navigate(['/pages/certificate']);
  }

  async deleteTemplate(): Promise<void> {
    if (!this.id) {
      return;
    }

    const dialogRef = this.dialog.open(FuseDeleteDialogComponent, {
      panelClass: 'unsave-form-dialog',
      width: '300px',
      data: {title: 'Delete', message: 'deleteConfirm'}
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (confirmed) {
      await firstValueFrom(this.certificateTemplateService.deleteTemplate(this.id));
      this.router.navigate(['/pages/certificate']);
    }
  }

  ngOnDestroy(): void {
    this.templateSubscription?.unsubscribe();
    this.designer?.destroy();
  }

  setOrientation(portrait: boolean): void {
    if (!this.designer) {
      return;
    }

    const template = this.designer.getTemplate();

    const width = portrait ? 210 : 297;
    const height = portrait ? 297 : 210;

    template.basePdf = {
      width,
      height,
      padding: [0, 0, 0, 0],
    };

    for (const page of template.schemas) {
      const background = page.find(
        schema => schema.name === this.BACKGROUND_SCHEMA_NAME
      );

      if (background) {
        background.position = { x: 0, y: 0 };
        background.width = width;
        background.height = height;
      }
    }

    this.designer.updateTemplate(template);
    this.isPortrait = portrait;
  }

  private addDynamicTextField(name: CertificateTemplateField, previewContent: string): void {
    if (!this.designer) {
      return;
    }

    const template: any = this.designer.getTemplate();

    const page = template.schemas[0];

    const alreadyExists = page.some(
      schema => schema.name === name,
    );

    if (alreadyExists) {
      console.warn(`Certificate field "${name}" already exists`,);
      return;
    }

    page.push({
      name,
      type: 'text',

      content: previewContent,

      position: {
        x: (template.basePdf.width - 150) / 2,
        y: 90,
      },

      width: 150,
      height: 15,

      rotate: 0,

      alignment: 'center',
      verticalAlignment: 'middle',

      fontSize: 24,

      textFormat: 'plain',
      overflow: 'visible',
      fontVariantFallback: 'synthetic',

      lineHeight: 1,
      characterSpacing: 0,

      fontColor: '#000000',
      backgroundColor: '',

      borderColor: '#000000',

      borderWidth: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },

      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },

      opacity: 1,

      strikethrough: false,
      underline: false,

      required: false,
    });

    this.designer.updateTemplate(template);
  }

  addDynamicField(config: CertificateFieldConfig): void {
      this.addDynamicTextField(
        config.field,
        config.preview,
      );
  }

  addStaticText(): void {
    if (!this.designer) {
      return;
    }

    const template: any = this.designer.getTemplate();

    template.schemas[0].push({
      ...structuredClone(text.propPanel.defaultSchema),
      name: `text_${crypto.randomUUID()}`,
      content: this.translate.instant('certificate.text'),
      position: {x: (template.basePdf.width - 150) / 2, y: 90},
      width: 150,
      height: 15,
      fontSize: 24,
      alignment: 'center',
      verticalAlignment: 'middle',
      readOnly: true,
      required: false,
    });

    this.designer.updateTemplate(template);
  }

  async onBackgroundSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !this.designer) {
      return;
    }

    if (
      file.type !== 'image/png' &&
      file.type !== 'image/jpeg'
    ) {
      return;
    }
    this.selectedBackgroundFile = file;
    const content = await this.readFileAsDataUrl(file);

    const template: any = this.designer.getTemplate();
    const page = template.schemas[0];

    const oldBackgroundIndex = page.findIndex(
      schema => schema.name === this.BACKGROUND_SCHEMA_NAME,
    );

    if (oldBackgroundIndex !== -1) {
      page.splice(oldBackgroundIndex, 1);
    }

    page.unshift({
      name: this.BACKGROUND_SCHEMA_NAME,
      type: 'image',
      content,

      position: {
        x: 0,
        y: 0,
      },

      width: template.basePdf.width,
      height: template.basePdf.height,

      objectFit: 'cover',
      objectPosition: 'center center',

      readOnly: true,
      required: false,
    });

    this.designer.updateTemplate(template);

    input.value = '';
  }

  get hasBackground(): boolean {
    return this.designer?.getTemplate().schemas[0]?.some(
      schema => schema.name === this.BACKGROUND_SCHEMA_NAME,
    ) ?? false;
  }

  removeBackground(): void {
    if (!this.designer) {
      return;
    }

    const template = this.designer.getTemplate();
    const page = template.schemas[0];

    const backgroundIndex = page.findIndex(
      schema => schema.name === this.BACKGROUND_SCHEMA_NAME,
    );

    if (backgroundIndex === -1) {
      return;
    }

    page.splice(backgroundIndex, 1);
    this.selectedBackgroundFile = undefined;

    this.designer.updateTemplate(template);
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }

        reject(new Error('Failed to read background image'));
      };

      reader.onerror = () => {
        reject(reader.error ?? new Error('Failed to read background image'));
      };

      reader.readAsDataURL(file);
    });
  }

  async onImageSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file || !this.designer) {
    return;
  }

  if (
    file.type !== 'image/png' &&
    file.type !== 'image/jpeg'
  ) {
    return;
  }

  const imageId = crypto.randomUUID();
    this.imageFiles.set(imageId, file);

    const content = await this.readFileAsDataUrl(file);

    const template = this.designer.getTemplate();
    const page = template.schemas[0];

    page.push({
      name: `image_${imageId}`,
      type: 'image',

      content,

      position: {
        x: 20,
        y: 20,
      },

      width: 40,
      height: 40,

      objectFit: 'contain',
      objectPosition: 'center center',

      rotate: 0,
      opacity: 1,

      required: false,
    });

    this.designer.updateTemplate(template);

    input.value = '';
  }

  async downloadExamplePdf(): Promise<void> {
    if (!this.designer) {
      return;
    }

    try {
      const template = this.designer.getTemplate();

      const inputs: Record<string, string> = {};

      for (const page of template.schemas) {
        for (const schema of page) {
          if (schema.type === 'text') {
            inputs[schema.name] = schema.content ?? '';
          } else if (schema.type === 'image') {
            inputs[schema.name] = schema.content ?? '';
          }
        }
      }

      const exampleValues: Record<string, string> = {
        [CertificateTemplateField.UserFullName]: 'John Smith',
        [CertificateTemplateField.EventName]: 'Example Training Event',
        [CertificateTemplateField.EventDate]: '31. Oktober 2026',
        [CertificateTemplateField.OrganizationName]: 'Organization Name',
      };

      for (const [key, value] of Object.entries(exampleValues)) {
        if (key in inputs) {
          inputs[key] = value;
        }
      }

      const pdf = await generate({
        template,
        inputs: [inputs],
        plugins: {
          Text: text,
          Image: image,
          Line: line,
        },
        options: {
          font: await this.loadCertificateFonts(),
        },
      });

      const blob = new Blob([new Uint8Array(pdf)], {
        type: 'application/pdf',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `${this.name?.trim() || 'certificate'}-example.pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Failed to generate example PDF', error);
    }
  }

  addLine(): void {
    if (!this.designer) {
      return;
    }

    const template: any = this.designer.getTemplate();

    const pageWidth = template.basePdf.width;

    const lineWidth = Math.min(160, pageWidth - 20);

    template.schemas[0].push({
      ...structuredClone(line.propPanel.defaultSchema),

      name: `line_${crypto.randomUUID()}`,

      type: 'line',

      position: {
        x: (pageWidth - lineWidth) / 2,
        y: 90,
      },

      width: lineWidth,
      height: 0.5,

      color: '#000000',
      lineWidth: 0.5,

      readOnly: false,
      required: false,
    });

    this.designer.updateTemplate(template);
  }

  private rememberOriginalAssets(template: Template): void {
    this.originalAssetIds.clear();

    for (const page of template.schemas) {
      for (const schema of page) {
        const assetId = (schema as any).assetId;
        if (assetId) {
          this.originalAssetIds.add(String(assetId));
        }
      }
    }
  }

  private getAssetIds(template: Template): Set<string> {
    const ids = new Set<string>();

    for (const page of template.schemas) {
      for (const schema of page) {
        const assetId = (schema as any).assetId;

        if (assetId) {
          ids.add(String(assetId));
        }
      }
    }
    return ids;
  }

  back(): void {
    this.location.back();
  }

  private async loadCertificateFonts(): Promise<Font> {
    const [
      roboto,
      robotoBold,
      robotoItalic,

      montserrat,
      montserratBold,

      playfair,
      playfairItalic,
      playfairBold,

      calibri,
      calibriBold,
    ] = await Promise.all([
      fetch('/assets/fonts/Roboto-Regular.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/Roboto-Bold.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/Roboto-Italic.ttf')
        .then(res => res.arrayBuffer()),


      fetch('/assets/fonts/Montserrat-Regular.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/Montserrat-Bold.ttf')
        .then(res => res.arrayBuffer()),


      fetch('/assets/fonts/PlayfairDisplay-Regular.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/PlayfairDisplay-Italic.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/PlayfairDisplay-Bold.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/calibri-regular.ttf')
        .then(res => res.arrayBuffer()),

      fetch('/assets/fonts/calibri-bold.ttf')
        .then(res => res.arrayBuffer()),
    ]);

    return {
      Roboto: {
        data: roboto,
        fallback: true,
      },

      'Roboto Bold': {
        data: robotoBold,
      },

      'Roboto Italic': {
        data: robotoItalic,
      },

      Montserrat: {
        data: montserrat,
      },

      'Montserrat Bold': {
        data: montserratBold,
      },

      'Playfair Display': {
        data: playfair,
      },

      'Playfair Display Italic': {
        data: playfairItalic,
      },

      'Playfair Display Bold': {
        data: playfairBold,
      },

      'Calibri': {
        data: calibri,
      },

      'Calibri Bold': {
        data: calibriBold,
      }
    };
  }
}
